import type { BrandSurface, Identity, Surface } from "../../../shared/types";

const DEFAULT_LINKEDIN_URL = "https://www.linkedin.com/feed/";

type IdentityLike = Pick<Identity, "kind" | "platform_kind" | "canonical_url" | "status">;
type BrandSurfaceLike = Pick<BrandSurface, "kind" | "canonical_url" | "status">;

function safeHttpUrl(rawValue: string | null | undefined): string | null {
  if (!rawValue?.trim()) return null;
  try {
    const parsed = new URL(rawValue);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function preferredLinkedInIdentityKinds(surface: Surface | string | null | undefined): Identity["kind"][] {
  if (surface === "linkedin_personal") return ["person", "agent", "brand", "shared"];
  if (surface === "linkedin_derivative") return ["brand", "shared", "person", "agent"];
  return ["brand", "shared", "person", "agent"];
}

function linkedInLabelForKind(kind: Identity["kind"]): string {
  switch (kind) {
    case "brand":
    case "shared":
      return "Open LinkedIn (Brand Page)";
    case "person":
    case "agent":
      return "Open LinkedIn (Personal)";
    default:
      return "Open LinkedIn";
  }
}

function surfaceKindsForLinkedIn(surface: Surface | string | null | undefined): string[] {
  if (surface === "linkedin_personal") return ["linkedin_personal", "linkedin_community", "linkedin"];
  if (surface === "linkedin_derivative") return ["linkedin_derivative", "linkedin_community", "linkedin"];
  return ["linkedin", "linkedin_community"];
}

export function resolveLinkedInDestination(
  surface: Surface | string | null | undefined,
  options: {
    surfaces?: BrandSurfaceLike[] | null | undefined;
    identities?: IdentityLike[] | null | undefined;
  },
): { url: string; label: string } {
  const canonicalSurfaceUrl = surfaceKindsForLinkedIn(surface)
    .flatMap((kind) =>
      (options.surfaces ?? [])
        .filter((candidate) => candidate.kind === kind && candidate.status !== "retired")
        .flatMap((candidate) => {
          const canonicalUrl = safeHttpUrl(candidate.canonical_url);
          return canonicalUrl ? [canonicalUrl] : [];
        }),
    )[0];

  if (canonicalSurfaceUrl) {
    return {
      url: canonicalSurfaceUrl,
      label: "Open LinkedIn (Brand Page)",
    };
  }

  const activeLinkedinIdentities = (options.identities ?? []).flatMap((identity) => {
    if (identity.platform_kind !== "linkedin" || identity.status === "retired") return [];
    const canonicalUrl = safeHttpUrl(identity.canonical_url);
    return canonicalUrl ? [{ kind: identity.kind, canonicalUrl }] : [];
  });

  for (const kind of preferredLinkedInIdentityKinds(surface)) {
    const match = activeLinkedinIdentities.find((identity) => identity.kind === kind);
    if (match) {
      return {
        url: match.canonicalUrl,
        label: linkedInLabelForKind(kind),
      };
    }
  }

  return {
    url: DEFAULT_LINKEDIN_URL,
    label: "Open LinkedIn",
  };
}
